import axios from 'axios';
import qs from 'qs';
import NetworkError from './NetworkError';
import Log from '../logger/Log';
const TAG = 'network/Api';
export const ERROR_RESPONSE_401 = 401;
export const ERROR_RESPONSE_403 = 403;
export const ERROR_RESPONSE_404 = 404;
export const ERROR_RESPONSE_503 = 503;
export const ERROR_NETWORK = 1000;
export const ERROR_RESPONSE_FORMAT = 1001;
export var ContentType;
(function (ContentType) {
    ContentType["FORM_URLENCODED"] = "application/x-www-form-urlencoded";
    ContentType["MULTIPART_FORM_DATA"] = "multipart/form-data";
    ContentType["APPLICATION_JSON"] = "application/json";
})(ContentType || (ContentType = {}));
class Api {
    constructor(httpOptions) {
        this.contentType = ContentType.FORM_URLENCODED;
        this.enableCache = true;
        this.retryCount = 0;
        const { idg } = this;
        this.options = Api.generateOptions(idg, httpOptions);
        const { baseURL, timeout } = this.options;
        this.agent = axios.create({
            baseURL,
            timeout,
        });
        this.agent.interceptors.request.use((requestConfig) => {
            const config = this.requestHandler(requestConfig);
            if (this.enableCache) {
                config.id = config.params.IDG_CACHE_ID;
                delete config.params.IDG_CACHE_ID;
            }
            if (this.retryCount) {
                config.retry = config.params.IDG_RETRY_COUNT;
                delete config.params.IDG_RETRY_COUNT;
            }
            return config;
        }, (error) => {
            this.removeCache(error);
            this.errorHandler(error);
        });
        this.agent.interceptors.response.use((response) => {
            this.removeCache(response);
            return this.responseHandler(response);
        }, (error) => {
            this.removeCache(error);
            const retryRes = this.retry(error);
            if (retryRes) {
                return retryRes;
            }
            return this.errorHandler(error);
        });
    }
    /**
     * 生成options
     * @param idg Idg
     * @param httpOptions 配置
     */
    static generateOptions(idg, httpOptions) {
        const options = Object.assign(Api.DEFAULT_OPTIONS, {
            baseURL: idg.root.getBaseURL(idg),
        }, httpOptions);
        return options;
    }
    /**
     * Generates data 根据contentType生成data
     * @param data
     * @returns data
     */
    static generateData(data, contentType) {
        if (contentType === ContentType.FORM_URLENCODED) {
            return qs.stringify(data);
        }
        if (contentType === ContentType.MULTIPART_FORM_DATA) {
            const formData = new FormData();
            Api.makeFormData(data, formData);
            return formData;
        }
        if (contentType === ContentType.APPLICATION_JSON) {
            return JSON.stringify(data);
        }
    }
    /**
     * 生成FormData处理文件数据
     * @param obj 对象数据
     * @param formData FormData实例
     */
    static makeFormData(obj, formData) {
        const data = [];
        if (obj instanceof File) {
            data.push({ key: '', value: obj });
        }
        else if (obj instanceof Array) {
            for (let j = 0, len = obj.length; j < len; j++) {
                const arr = Api.makeFormData(obj[j]);
                for (let k = 0, l = arr.length; k < l; k++) {
                    const key = !!formData ? j + arr[k].key : '[' + j + ']' + arr[k].key;
                    data.push({ key, value: arr[k].value });
                }
            }
        }
        else if (typeof obj === 'object') {
            Object.keys(obj).forEach((j) => {
                const arr = Api.makeFormData(obj[j]);
                for (let k = 0, l = arr.length; k < l; k++) {
                    const key = !!formData ? j + arr[k].key : '[' + j + ']' + arr[k].key;
                    data.push({ key, value: arr[k].value });
                }
            });
        }
        else {
            data.push({ key: '', value: obj });
        }
        if (!!formData) {
            for (let i = 0, len = data.length; i < len; i++) {
                formData.append(data[i].key, data[i].value);
            }
        }
        else {
            return data;
        }
    }
    /**
     * Requests api 发送请求
     * @template R
     * @param requestConfig
     * @returns request
     */
    request(requestConfig) {
        return this._request(requestConfig);
    }
    /**
     * 获取中台特有的header
     */
    getIdgHeaders() {
        const headers = {
            RequestStack: JSON.stringify(this.idg.getRequestStack()),
        };
        const token = this.idg.root.getToken();
        if (token) {
            headers.Authorization = token;
        }
        return headers;
    }
    /**
     * 默认处理401、403、404
     *
     * @param error 请求错误
     */
    errorHandler(error) {
        if (error.response) {
            if (error.response.status === 403) {
                this.idg.root.redirectLogin();
                return Promise.reject(new NetworkError(ERROR_RESPONSE_403, '403 NO ACCESS'));
            }
            if (error.response.status === 401) {
                this.idg.root.redirectLogin();
                return Promise.reject(new NetworkError(ERROR_RESPONSE_401, '401 Unauthorized'));
            }
            if (error.response.status === 404) {
                return Promise.reject(new NetworkError(ERROR_RESPONSE_404, '404 Not Found'));
            }
            if (error.response.status === 503) {
                return Promise.reject(new NetworkError(ERROR_RESPONSE_404, 'Service Unavaliable'));
            }
        }
        return Promise.reject(new NetworkError(ERROR_NETWORK, 'network error'));
    }
    /**
     * 默认header中Authorization带token
     * 默认header中RequestStack带请求栈
     * @param requestConfig 请求配置
     */
    requestHandler(requestConfig) {
        const idgHeaders = this.getIdgHeaders();
        requestConfig.headers = Object.assign(requestConfig.headers, idgHeaders);
        if (!requestConfig.params) {
            requestConfig.params = {};
        }
        requestConfig.headers['Content-Type'] = requestConfig.headers['Content-Type'] || this.contentType;
        requestConfig.data = Api.generateData(requestConfig.data, requestConfig.headers['Content-Type']);
        return requestConfig;
    }
    /**
     * 默认中台返回格式
     * 正确：{ state: 1, data: xx }
     * 错误：{ state: 1001, msg: 'xxxx'}
     * @param response http请求返回
     */
    responseHandler(response) {
        if (!response.data) {
            return Promise.reject(new NetworkError(ERROR_RESPONSE_FORMAT, 'no data'));
        }
        const res = response.data;
        if (!res.hasOwnProperty('state') && !res.hasOwnProperty('ret')) {
            return Promise.reject(new NetworkError(ERROR_RESPONSE_FORMAT, 'error response format, should like {state: xx, data: xx}'));
        }
        if (res.state !== 1) {
            return Promise.reject(new NetworkError(res.state, res.msg));
        }
        return res.data;
    }
    /**
     * Removes cache 清除缓存请求
     * @param response
     * @returns
     */
    removeCache(response) {
        if (!this.enableCache) {
            return;
        }
        const config = response.config;
        const index = Api.cacheRequests.findIndex((cacheRequest) => {
            return cacheRequest.id === config.id;
        });
        if (index >= 0) {
            Api.cacheRequests.splice(index, 1);
        }
        else {
            Log.warn(TAG, 'api response not cached');
        }
    }
    /**
     * Retrys api 断线重试
     * @param error
     * @returns retry
     */
    retry(error) {
        if (!this.retryCount) {
            return;
        }
        const config = error.config;
        if (!config.params) {
            config.params = {};
        }
        config.params.IDG_RETRY_COUNT = config.retry || 0;
        if (config.params.IDG_RETRY_COUNT === this.retryCount) {
            return;
        }
        config.params.IDG_RETRY_COUNT = config.params.IDG_RETRY_COUNT + 1;
        config.url = config.url.replace(config.baseURL, '');
        if (config.data) {
            config.data = qs.parse(config.data);
        }
        return this.agent(error.config);
    }
    _request(requestConfig) {
        const headers = this.getIdgHeaders();
        const { method, url, data } = requestConfig;
        if (!this.enableCache) {
            return this.agent.request(requestConfig);
        }
        // header 和 数据相同的时候直接返回
        const cacahe = Api.cacheRequests.find((cacheRequest) => {
            return this.equealRequest(Object.assign({}, requestConfig, { headers }), cacheRequest);
        });
        if (cacahe) {
            return cacahe.promise;
        }
        else {
            const id = ++Api.id;
            if (!requestConfig.params) {
                requestConfig.params = {};
            }
            requestConfig.params.IDG_CACHE_ID = id;
            const promise = this.agent.request(requestConfig);
            Api.cacheRequests.push({
                id,
                requestConfig: {
                    headers,
                    data,
                    url,
                    method,
                },
                promise,
            });
            return promise;
        }
    }
    /**
     * Equeals request 判断两个请求是否一样
     * @param requestConfig
     * @param cacheRequest
     * @returns
     */
    equealRequest(requestConfig, cacheRequest) {
        const headers = requestConfig.headers;
        const data = requestConfig.data;
        const euqalHeaders = JSON.stringify(headers) === JSON.stringify(cacheRequest.requestConfig.headers);
        const equalData = qs.stringify(data) === qs.stringify(cacheRequest.requestConfig.data);
        const equalMethod = requestConfig.method === cacheRequest.requestConfig.method;
        const equalUrl = requestConfig.url === cacheRequest.requestConfig.url;
        return euqalHeaders && equalData && equalMethod && equalUrl;
    }
}
Api.id = 0;
Api.cacheRequests = [];
Api.DEFAULT_OPTIONS = {
    timeout: 5000,
};
export default Api;
